import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LRFCobenefit, LRFProjectType, ProjectType } from "../data/types/sum";
import { EligibleProject } from "../data/api/types";
import { SRecoverable, SSa2Wrapper } from "../data/Recoverable";
import {
  deserialiseRecoverable,
  deserialiseSa2Wrapper
} from "../data/Recoverable";
import {
  DirectSoilMeasurementResponse,
  HerdComposition,
  HerdResponseWithSa2
} from "../data/api/soil/types";
import { SoilIndexResponseIndex } from "../data/api/soil_idx";
import { PlantationProjectType } from "../data/types/sum";
import { PlantationProjectResponse } from "../data/api/plantation/types";

export type SoilCarbonProject = {
  creditUnitsProject?: number;
  creditUnitsAnnual?: number;
  selectedTarget?: number;
  soilCarbonPercentage?: number;
  soilCarbonResp?: DirectSoilMeasurementResponse;
};

export type SoilCarbonIndex = {
  soilCarbonIndex?: Array<SoilIndexResponseIndex>;
};

export type PlantationProject = {
  type: ProjectType;
  duration: number;
  creditUnitsProject?: number;
  creditUnitsAnnual?: number;
  area: number;
  plantationResp?: PlantationProjectResponse;
};

export type HerdManagementProject = {
  creditUnitsProject?: number;
  creditUnitsAnnual?: number;
  selectedHerdComposition?: HerdComposition;
  selectedEfficiency?: number;
  herdResps?: Array<HerdResponseWithSa2<number>>;
};

export type GenericPlantationUpdate = {
  creditUnitsProject: number;
  creditUnitsAnnual: number;
  plantationProjectType: PlantationProjectType;
  completed: boolean;
};

export type LRFEstimates = Record<LRFProjectType, LRFCobenefit>;

export type LRFEstimatePayloadAction = {
  projectType: LRFProjectType;
  estimate: LRFCobenefit;
};

export type ShortToLongConversionSummary = {
  accusTotal: number;
  accusPerYearPerHectare: number;
};

export type TransitioningToPermanentForestSummary = {
  accusTotal: number;
  accusPerYearPerHectare: number;
};

export type ContinuingRotationalHarvestSummary = {
  accusTotal: number;
  accusPerYearPerHectare: number;
};

export type MethodDiscoveryState = {
  selectedProject?: ProjectType;
  soilEstimates: Array<SRecoverable<SSa2Wrapper<EligibleProject>>>;
  vegEstimates: Array<EligibleProject>;
  plantationEstimates: Array<EligibleProject>;
  allowablePlantationProjectTypes: Array<PlantationProjectType>;
  plantationProject?: PlantationProject;
  farmingForestryProject?: PlantationProject;
  shortToLongConversionSummary?: ShortToLongConversionSummary;
  transitioningToPermanentForestSummary?: TransitioningToPermanentForestSummary;
  continuingRotationalHarvestSummary?: ContinuingRotationalHarvestSummary;
  selectedSpecies?: string | undefined;
  soilCarbonProject?: SoilCarbonProject;
  soilCarbonIndex?: Array<SoilIndexResponseIndex>;
  herdManagementProject?: HerdManagementProject;
  lrfEstimates?: Partial<LRFEstimates>;
  plantationTotalHa?: number;
};

const INITIAL_STATE: MethodDiscoveryState = {
  soilEstimates: [],
  vegEstimates: [],
  plantationEstimates: [],
  allowablePlantationProjectTypes: []
};

const methodDiscoverySlice = createSlice({
  name: "methodDiscovery",
  initialState: INITIAL_STATE,
  reducers: {
    setVegEstimates(state, action: PayloadAction<EligibleProject[]>) {
      state.vegEstimates = action.payload;
    },
    setPlantationEstimates(state, action: PayloadAction<EligibleProject[]>) {
      state.plantationEstimates = action.payload;
    },
    setPlantationEstimateUpdate(
      state,
      action: PayloadAction<GenericPlantationUpdate>
    ) {
      let stateEstIndex = state.plantationEstimates.findIndex(
        p => p.type.type === action.payload.plantationProjectType
      );
      state.plantationEstimates[stateEstIndex].creditUnitsAnnual =
        action.payload.creditUnitsAnnual;
      state.plantationEstimates[stateEstIndex].creditUnitsProject =
        action.payload.creditUnitsProject;
      state.plantationEstimates[stateEstIndex].completed =
        action.payload.completed;
    },
    appendSoilEstimates(
      state,
      action: PayloadAction<Array<SRecoverable<SSa2Wrapper<EligibleProject>>>>
    ) {
      action.payload.forEach(a => {
        let estimate = deserialiseRecoverable(a).map(deserialiseSa2Wrapper);

        if (
          estimate.recover(
            a => a,
            a => a
          ).data.creditUnitsProject > 0
        ) {
          state.soilEstimates = state.soilEstimates.concat([a]);
        }
      });
    },
    setSoilCarbonProject(state, action: PayloadAction<SoilCarbonProject>) {
      state.soilCarbonProject = action.payload;
    },
    setSoilCarbonIndex(
      state,
      action: PayloadAction<Array<SoilIndexResponseIndex>>
    ) {
      state.soilCarbonIndex = action.payload;
    },
    setSoilCarbonProjectAPIResp(
      state,
      action: PayloadAction<DirectSoilMeasurementResponse>
    ) {
      if (state.soilCarbonProject)
        state.soilCarbonProject.soilCarbonResp = action.payload;
      else
        state.soilCarbonProject = {
          soilCarbonPercentage: action.payload.polygonOCPercAverage,
          soilCarbonResp: action.payload
        };
    },
    setHerdManagementProject(
      state,
      action: PayloadAction<HerdManagementProject>
    ) {
      state.herdManagementProject = action.payload;
    },

    setAllowablePlantationProjectTypes(
      state,
      action: PayloadAction<PlantationProjectType[]>
    ) {
      state.allowablePlantationProjectTypes = action.payload;
    },

    setPlantationProject(state, action: PayloadAction<PlantationProject>) {
      state.plantationProject = action.payload;
    },

    setFarmingForestryProject(state, action: PayloadAction<PlantationProject>) {
      state.farmingForestryProject = action.payload;
    },

    setSelectedSpeciesForPlantation(state, action: PayloadAction<string>) {
      state.selectedSpecies = action.payload;
    },

    setPlantationTotalHa(state, action: PayloadAction<number>) {
      state.plantationTotalHa = action.payload;
    },

    setPlantationShortToLongConversionSummary(
      state,
      action: PayloadAction<ShortToLongConversionSummary>
    ) {
      state.shortToLongConversionSummary = action.payload;
    },

    setPlantationContinuingRotationalHarvestSummary(
      state,
      action: PayloadAction<ContinuingRotationalHarvestSummary>
    ) {
      state.continuingRotationalHarvestSummary = action.payload;
    },

    setPlantationTransitioningToPermanentForestSummary(
      state,
      action: PayloadAction<TransitioningToPermanentForestSummary>
    ) {
      state.transitioningToPermanentForestSummary = action.payload;
    },

    resetState: state => INITIAL_STATE,

    setLRFEstimate(state, action: PayloadAction<LRFEstimatePayloadAction>) {
      if (state.lrfEstimates) {
        state.lrfEstimates[action.payload.projectType] =
          action.payload.estimate;
      } else {
        state.lrfEstimates = {
          [action.payload.projectType]: action.payload.estimate
        };
      }
    }
  }
});

// Extract the action creators object and the reducer
const { actions, reducer } = methodDiscoverySlice;
// Extract and export each action creator by name
export const {
  setVegEstimates,
  setPlantationEstimates,
  appendSoilEstimates,
  setSoilCarbonProject,
  setSoilCarbonIndex,
  setSoilCarbonProjectAPIResp,
  setHerdManagementProject,
  setAllowablePlantationProjectTypes,
  setPlantationProject,
  setFarmingForestryProject,
  setSelectedSpeciesForPlantation,
  setPlantationContinuingRotationalHarvestSummary,
  setPlantationShortToLongConversionSummary,
  setPlantationTransitioningToPermanentForestSummary,
  setPlantationEstimateUpdate,
  setPlantationTotalHa,
  setLRFEstimate,
  resetState
} = actions;
// Export the reducer, either as a default or named export
export default reducer;
